/**
 * Typed wrappers for service-mail draft endpoints (port 3001 by default).
 *
 * Used by the parcel-booking wizard and the My Drafts page.
 *
 * Two endpoint families:
 *
 *   1. Operator-scoped `/v1/drafts` — `createDraft`, `listDrafts`, etc. These
 *      are decorated with `@RequirePermission('Mail', '<verb>')` and
 *      `@OperatorScoped()`. They reject citizen JWTs (no operator context).
 *      Used by the parcel-booking wizard at the moment because the wizard
 *      autosaves through the same path the staff registration UI uses.
 *
 *   2. Citizen-scoped `/v1/drafts/me` — `listMyDrafts`, `discardMyDraft`,
 *      `resumeMyDraft`. Decorated with `@RequirePermission('Mail', '<verb>-own')`
 *      and filter rows by the JWT `customerId` claim (no operator context).
 *      Used by `CustomerDraftsPage` to surface the citizen's own in-flight
 *      drafts.
 *
 * Shapes mirror `apps/service-mail/src/modules/draft/draft.dto.ts`.
 */
import {
  requestJson,
  type RequestJsonOptions,
} from '../api-client';
import { getCustomerToken } from '../auth/tokenStore';
import { env } from '../env';

export type ServiceClass = 'STANDARD' | 'EXPRESS' | 'EMS' | 'REGISTERED_MAIL';
export type DraftStep = 'SENDER' | 'RECEIVER' | 'PACKAGE' | 'PRICING' | 'REVIEW';
export type DraftStatus = 'DRAFT' | 'SUBMITTED' | 'EXPIRED' | 'DISCARDED';

export interface DraftParty {
  readonly name: string;
  readonly phone: string;
  readonly email?: string;
  readonly addressLine1: string;
  readonly addressLine2?: string;
  readonly city: string;
  readonly state: string;
  readonly postalCode?: string;
  readonly countryCode?: string;
}

export interface DraftPackage {
   
  readonly weightGrams?: number;
  readonly lengthCm?: number;
  readonly widthCm?: number;
  readonly heightCm?: number;
  readonly contentDescription: string;
  readonly declaredValueAmount?: string;
  readonly declaredValueCurrency?: string;
  readonly declaredItems?: readonly string[];
  readonly notes?: string;
}

export interface DraftPricing {
  readonly amount?: string;
  readonly currency?: string;
  readonly quoteId?: string;
}

export interface DraftPayload {
  readonly sender?: DraftParty;
  readonly receiver?: DraftParty;
  readonly package?: DraftPackage;
  readonly pricing?: DraftPricing;
  readonly serviceClass?: ServiceClass;
}

export interface CreateDraftRequest {
  readonly payload: DraftPayload;
  readonly step: DraftStep;
  readonly idempotencyKey?: string;
}

export interface DraftResponse {
  readonly id: string;
  readonly tenantId: string;
  readonly operatorId?: string | null;
  readonly status: DraftStatus;
  readonly step: DraftStep;
  readonly payload: DraftPayload;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly expiresAt?: string | null;
}

export interface ListDraftsResponse {
  readonly items: readonly DraftResponse[];
  // eslint-disable-next-line upmp/no-number-money -- row count, not money
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

export interface SubmitDraftResponse {
  readonly id: string;
  readonly trackingNumber: string;
  readonly status: string;
  readonly serviceClass: string;
  readonly draftId: string;
  readonly registeredAt: string;
}

type CallOpts = Pick<RequestJsonOptions, 'fetchImpl' | 'signal'> & {
  readonly bearerToken?: string;
};

function bearerOf(opts: CallOpts): string | undefined {
  return opts.bearerToken ?? getCustomerToken() ?? undefined;
}

export async function createDraft(
  body: CreateDraftRequest,
  options: CallOpts = {},
): Promise<DraftResponse> {
  const { bearerToken: _ignore, ...rest } = options;
  return requestJson<DraftResponse>({
    method: 'POST',
    baseUrl: env.serviceMailBaseUrl,
    path: '/v1/drafts',
    body,
    bearerToken: bearerOf(options),
    ...rest,
  });
}

export async function listDrafts(
  query: { readonly status?: DraftStatus; readonly page?: number; readonly pageSize?: number } = {},
  options: CallOpts = {},
): Promise<ListDraftsResponse> {
  const params = new URLSearchParams();
  if (query.status) params.set('status', query.status);
  if (query.page) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  const suffix = params.toString();
  const path = suffix.length > 0 ? `/v1/drafts?${suffix}` : '/v1/drafts';
  const { bearerToken: _ignore, ...rest } = options;
  return requestJson<ListDraftsResponse>({
    method: 'GET',
    baseUrl: env.serviceMailBaseUrl,
    path,
    bearerToken: bearerOf(options),
    ...rest,
  });
}

export async function getDraft(
  id: string,
  options: CallOpts = {},
): Promise<DraftResponse> {
  const { bearerToken: _ignore, ...rest } = options;
  return requestJson<DraftResponse>({
    method: 'GET',
    baseUrl: env.serviceMailBaseUrl,
    path: `/v1/drafts/${encodeURIComponent(id)}`,
    bearerToken: bearerOf(options),
    ...rest,
  });
}

export async function discardDraft(
  id: string,
  options: CallOpts = {},
): Promise<DraftResponse> {
  const { bearerToken: _ignore, ...rest } = options;
  return requestJson<DraftResponse>({
    method: 'DELETE',
    baseUrl: env.serviceMailBaseUrl,
    path: `/v1/drafts/${encodeURIComponent(id)}`,
    bearerToken: bearerOf(options),
    ...rest,
  });
}

export async function resumeDraft(
  id: string,
  options: CallOpts = {},
): Promise<DraftResponse> {
  const { bearerToken: _ignore, ...rest } = options;
  return requestJson<DraftResponse>({
    method: 'POST',
    baseUrl: env.serviceMailBaseUrl,
    path: `/v1/drafts/${encodeURIComponent(id)}/resume`,
    bearerToken: bearerOf(options),
    ...rest,
  });
}

export async function submitDraft(
  id: string,
  options: CallOpts = {},
): Promise<SubmitDraftResponse> {
  const { bearerToken: _ignore, ...rest } = options;
  return requestJson<SubmitDraftResponse>({
    method: 'POST',
    baseUrl: env.serviceMailBaseUrl,
    path: `/v1/drafts/${encodeURIComponent(id)}/submit`,
    bearerToken: bearerOf(options),
    ...rest,
  });
}

// ---------------------------------------------------------------------------
// Citizen-scoped variants — `/v1/drafts/me`.
//
// These hit CitizenDraftController on service-mail. The server filters by
// the JWT `customerId` claim, so the caller never has to pass a customer id
// explicitly. Cross-customer access yields 404 (not 403) by design.
// ---------------------------------------------------------------------------

export async function listMyDrafts(
  query: { readonly status?: DraftStatus; readonly page?: number; readonly pageSize?: number } = {},
  options: CallOpts = {},
): Promise<ListDraftsResponse> {
  const params = new URLSearchParams();
  if (query.status) params.set('status', query.status);
  if (query.page) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  const suffix = params.toString();
  const path = suffix.length > 0 ? `/v1/drafts/me?${suffix}` : '/v1/drafts/me';
  const { bearerToken: _ignore, ...rest } = options;
  return requestJson<ListDraftsResponse>({
    method: 'GET',
    baseUrl: env.serviceMailBaseUrl,
    path,
    bearerToken: bearerOf(options),
    ...rest,
  });
}

export async function discardMyDraft(
  id: string,
  options: CallOpts = {},
): Promise<DraftResponse> {
  const { bearerToken: _ignore, ...rest } = options;
  return requestJson<DraftResponse>({
    method: 'DELETE',
    baseUrl: env.serviceMailBaseUrl,
    path: `/v1/drafts/me/${encodeURIComponent(id)}`,
    bearerToken: bearerOf(options),
    ...rest,
  });
}

export async function resumeMyDraft(
  id: string,
  options: CallOpts = {},
): Promise<DraftResponse> {
  const { bearerToken: _ignore, ...rest } = options;
  return requestJson<DraftResponse>({
    method: 'POST',
    baseUrl: env.serviceMailBaseUrl,
    path: `/v1/drafts/me/${encodeURIComponent(id)}/resume`,
    bearerToken: bearerOf(options),
    ...rest,
  });
}
